import React from "react";
import { makeStyles } from "@mui/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MailIcon from "@mui/icons-material/Mail";
import MoreIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Collapse from "@mui/material/Collapse";
import GroupsIcon from "@mui/icons-material/Groups";
import Chip from "@mui/material/Chip";
import AlarmIcon from "@mui/icons-material/Alarm";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Avatar from "@mui/material/Avatar";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SettingsIcon from "@mui/icons-material/Settings";
import { alpha } from "@mui/material/styles";
import { useHistory, useLocation } from "react-router-dom";
import useApp from "../../store/contexts/AppContext";
import { ROUTES } from "../../routes/User/paths";
import RoomModal from "../RoomModal/RoomModal";
import RoomJoinRequestsModal from "../RoomModal/RoomJoinRequestsModal";
import RoomTopMenuOptionsModal from "../RoomModal/RoomTopMenuOptionsModal";
import NotificationBell from "../Notifications/NotificationBell";
import capitalizeEachFirstWord from "../../utils/CapLetters";
import TruncateString from "../../utils/TruncateString";
import { ROOM_EXPIRED_VALUE, CHAT_ROOMS } from "../../config";
import MESSAGE from "../../config/messages";

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  appBar: {
    background: theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)}, ${alpha(theme.palette.primary.dark, 0.98)})`
      : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.7)}, ${alpha(theme.palette.background.paper, 0.8)})`,
    backdropFilter: "blur(30px) saturate(180%)",
    WebkitBackdropFilter: "blur(30px) saturate(180%)",
    boxShadow: theme.palette.mode === "dark"
      ? `0 4px 30px ${alpha(theme.palette.common.black, 0.5)}, inset 0 1px ${alpha(theme.palette.common.white, 0.1)}`
      : `0 4px 30px ${alpha(theme.palette.common.black, 0.1)}, inset 0 1px ${alpha(theme.palette.common.white, 0.5)}`,
    borderBottom: theme.palette.mode === "dark"
      ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
      : `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
  },
  toolbar: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    minHeight: "64px",
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(1, 1),
      minHeight: "56px",
    },
  },
  logo: {
    fontWeight: 900,
    fontSize: "1.5rem",
    letterSpacing: "0.02em",
    background: theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${theme.palette.common.white}, ${alpha(
          theme.palette.common.white,
          0.8,
        )})`
      : "linear-gradient(135deg, #d44402, #ff6f00)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem",
    },
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flex: "0 0 auto",
    zIndex: 2,
  },
  centerSection: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "40%",
    zIndex: 1,
    [theme.breakpoints.down("md")]: {
      maxWidth: "50%",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "55%",
    },
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flex: "0 0 auto",
    zIndex: 2,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  iconButton: {
    color: theme.palette.mode === "dark" ? "#FFFFFF !important" : "#2a3478 !important",
    transition: "all 0.3s ease",
    opacity: 1,
    filter: theme.palette.mode === "dark" ? "brightness(1.2)" : "none",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? alpha("#FFFFFF", 0.2)
        : alpha(theme.palette.primary.main, 0.15),
      transform: "scale(1.15)",
      filter: theme.palette.mode === "dark" ? "brightness(1.5)" : "none",
      boxShadow: theme.palette.mode === "dark"
        ? "0 0 8px rgba(255, 255, 255, 0.5)"
        : `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
    "& svg": {
      color: theme.palette.mode === "dark" ? "#FFFFFF !important" : "#2a3478 !important",
      fill: theme.palette.mode === "dark" ? "#FFFFFF !important" : "#2a3478 !important",
    },
  },
  roomInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 2),
    borderRadius: 12,
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.1)
      : alpha(theme.palette.common.black, 0.05),
    backdropFilter: "blur(10px)",
    border: theme.palette.mode === "dark"
      ? "none"
      : `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.5, 1),
    },
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.4, 0.75),
    },
  },
  roomName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.mode === "dark"
      ? theme.palette.common.white
      : "#2a3478",
    [theme.breakpoints.down("md")]: {
      fontSize: "0.85rem",
      maxWidth: "120px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      maxWidth: "80px",
    },
  },
  timerChip: {
    backgroundColor: theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.2)
      : alpha("#d44402", 0.15),
    color: theme.palette.mode === "dark"
      ? theme.palette.common.white
      : "#d44402",
    fontWeight: 700,
    "& .MuiAvatar-root": {
      backgroundColor: theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.3)
        : alpha("#d44402", 0.2),
    },
    [theme.breakpoints.down("md")]: {
      "& .MuiChip-label": {
        fontSize: "0.75rem",
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
      },
      "& .MuiAvatar-root": {
        width: 20,
        height: 20,
        "& svg": {
          fontSize: "0.9rem",
        },
      },
    },
    [theme.breakpoints.down("sm")]: {
      "& .MuiChip-label": {
        fontSize: "0.65rem",
      },
    },
  },
  sectionDesktop: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      gap: theme.spacing(1),
    },
  },
  sectionMobile: {
    display: "flex",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  collapseWrapper: {
    position: "relative",
  },
  headerToggleTab: {
    position: "absolute",
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.98,
    )}, ${alpha(theme.palette.primary.dark, 1)})`,
    color: "#fff",
    backgroundColor: alpha(theme.palette.primary.main, 0.9),
    border: "none",
    borderRadius: "0 0 12px 12px",
    padding: "2px 20px",
    cursor: "pointer",
    opacity: 0.25,
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.4)}`,
    transition: "opacity 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      opacity: 1,
      filter: "brightness(1.15)",
      boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.5)}`,
    },
  },
}));

export default function Header({
  toggleOnRoomCreationModal,
  setToggleOnRoomCreationModal,
  isCurrentUserMainAdmin,
}) {
  const history = useHistory();
  const location = useLocation();
  const {
    appState: { userData, activeChat, roomToolbox, waitingRoomParticipantRequests },
    updateExpiredActiveChat,
    showSnackbar,
    logoutUser,
    toggleRoomToolbox,
  } = useApp();

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [customRoomModalTabValue, setCustomRoomModalTabValue] =
    React.useState("1");
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  const [roomMenuOpen, setRoomMenuOpen] = React.useState(false);
  const [roomJoinRequestsOpen, setRoomJoinRequestsOpen] = React.useState(false);
  const [roomTopMenuOptionsOpen, setRoomTopMenuOptionsOpen] =
    React.useState(false);
  const [remainingTime, setRemainingTime] = React.useState(null);
  const [isCollapsed, setIsCollapsed] = React.useState(() => {
    const stored = localStorage.getItem("navbarCollapsed");
    return stored !== null ? stored === "true" : true;
  });

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const openRoomModal = () => {
    handleMenuClose();
    setRoomMenuOpen(true);
  };

  // Re-open the join requests popup whenever admin navigates to a new page
  React.useEffect(() => {
    if (waitingRoomParticipantRequests?.length > 0) {
      setRoomJoinRequestsOpen(true);
    }
  }, [location.pathname, waitingRoomParticipantRequests]);

  const openRoomJoinRequestsModal = () => {
    handleMenuClose();
    setRoomJoinRequestsOpen(true);
  };

  const openRoomTopMenuOptionsModal = () => {
    handleMenuClose();
    setRoomTopMenuOptionsOpen(true);
  };

  const goToDashboard = () => {
    return (window.location.href = ROUTES.dashboard);
  };

  const goToMessages = () => {
    return (window.location.href = ROUTES.messages);
  };

  const goToHomepage = () => {
    return (window.location.href = ROUTES.dashboard);
  };

  const goToAccountPage = () => {
    return (window.location.href = `/${userData?.username}`);
  };

  const goToBookmarks = () => {
    history.push(ROUTES.bookmarks);
  };

  const goToSettingsPage = () => {
    handleMenuClose();
    history.push(ROUTES.settings);
  };

  // Update remaining time
  React.useEffect(() => {
    const intervalId = setInterval(() => {
      if (
        activeChat &&
        activeChat.expiration_time &&
        activeChat.type === CHAT_ROOMS.TYPES.GROUP &&
        !activeChat.is_expired
      ) {
        const expirationTime = new Date(activeChat.expiration_time);
        const currentTime = new Date();
        const differenceInSeconds = Math.floor(
          (expirationTime - currentTime) / 1000,
        );

        if (differenceInSeconds <= 0) {
          clearInterval(intervalId);
          setRemainingTime(ROOM_EXPIRED_VALUE);
          updateExpiredActiveChat(activeChat.id);
          showSnackbar(MESSAGE.ROOM_HAS_EXPIRED);
          setTimeout(() => {
            goToDashboard();
          }, 3000);
        } else {
          const hours = Math.floor(differenceInSeconds / 3600);
          const minutes = Math.floor((differenceInSeconds % 3600) / 60);
          const seconds = differenceInSeconds % 60;

          if (hours > 0) {
            setRemainingTime(
              `${hours}:${minutes < 10 ? "0" : ""}${minutes}:${
                seconds < 10 ? "0" : ""
              }${seconds}`,
            );
          } else {
            setRemainingTime(`${minutes}:${seconds < 10 ? "0" : ""}${seconds}`);
          }
        }
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [activeChat]);

  React.useEffect(() => {
    if (toggleOnRoomCreationModal) {
      setCustomRoomModalTabValue("2");
      openRoomModal();
      setToggleOnRoomCreationModal(false);
    }
  }, [toggleOnRoomCreationModal]);

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={goToAccountPage}>
        Hi, {capitalizeEachFirstWord(userData?.username)}
      </MenuItem>
      <MenuItem onClick={goToAccountPage}>Account</MenuItem>
      <MenuItem onClick={goToSettingsPage}>Settings</MenuItem>
      <MenuItem onClick={() => logoutUser()}>Logout</MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={openRoomModal}>
        <IconButton
          edge="end"
          aria-label="Join private room"
          aria-controls={menuId}
          aria-haspopup="true"
          color="inherit"
          title="Room"
          sx={{ mr: "3px" }}
        >
          <GroupsIcon />
        </IconButton>
        <p>Rooms</p>
      </MenuItem>
      <MenuItem onClick={openRoomJoinRequestsModal}>
        <IconButton
          edge="end"
          aria-label="Users Room Join Requests"
          aria-controls={menuId}
          aria-haspopup="true"
          color="inherit"
          title="Room"
          sx={{ mr: "3px" }}
        >
          <ConnectWithoutContactIcon />
        </IconButton>
        <p>Room Join Requests</p>
      </MenuItem>
      <MenuItem>
        <NotificationBell />
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={goToBookmarks}>
        <IconButton
          aria-label="bookmarks"
          color="inherit"
          title="Bookmarks"
          sx={{ mr: "3px" }}
        >
          <BookmarkIcon />
        </IconButton>
        <p>Bookmarks</p>
      </MenuItem>
      <MenuItem onClick={goToMessages}>
        <IconButton
          aria-label="show 4 new mails"
          color="inherit"
          title="Messages"
        >
          <MailIcon />
        </IconButton>
        <p>Messages</p>
      </MenuItem>
      <MenuItem onClick={goToSettingsPage}>
        <IconButton
          aria-label="open settings"
          color="inherit"
          title="Settings"
        >
          <SettingsIcon />
        </IconButton>
        <p>Settings</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  return (
    <div className={classes.collapseWrapper}>
      <Collapse in={!isCollapsed}>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          {/* Left Section - Logo */}
          <div className={classes.leftSection}>
            <IconButton
              edge="start"
              aria-label="Dashboard"
              onClick={goToHomepage}
              color="inherit"
              className={classes.iconButton}
            >
              <Typography variant="h6" className={classes.logo}>
                {process.env.REACT_APP_NAME}
              </Typography>
            </IconButton>
          </div>

          {/* Center Section - Room Info (Only for group rooms) */}
          <div className={classes.centerSection}>
            {Object.keys(activeChat).length > 0 &&
              activeChat.type === CHAT_ROOMS.TYPES.GROUP && (
                <div className={classes.roomInfo}>
                  <IconButton
                    size="small"
                    onClick={openRoomTopMenuOptionsModal}
                    className={classes.iconButton}
                    title="Room menu"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>

                  <Typography className={classes.roomName}>
                    {TruncateString(activeChat.name)}
                  </Typography>

                  {remainingTime && (
                    <Chip
                      className={classes.timerChip}
                      avatar={
                        <Avatar>
                          <AlarmIcon />
                        </Avatar>
                      }
                      label={remainingTime}
                      size="small"
                      title="Remaining time"
                    />
                  )}

                  <IconButton
                    size="small"
                    onClick={toggleRoomToolbox}
                    className={classes.iconButton}
                    title={`${roomToolbox.isOpen ? "Close" : "Open"} Toolbox`}
                    sx={{ opacity: roomToolbox.isOpen ? 1 : 0.7 }}
                  >
                    <HomeRepairServiceIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
          </div>

          {/* Right Section - Desktop Icons */}
          <div className={classes.rightSection}>
            <div className={classes.sectionDesktop}>
            <IconButton
              edge="end"
              aria-label="Join private room"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={openRoomModal}
              className={classes.iconButton}
              title="Room"
            >
              <GroupsIcon />
            </IconButton>
            <IconButton
              edge="end"
              aria-label="Users Room Join Requests"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={openRoomJoinRequestsModal}
              className={classes.iconButton}
              title="Room Join Requests"
            >
              <ConnectWithoutContactIcon />
            </IconButton>
            {/* Notification Bell Component */}
            <NotificationBell />
            {/* Bookmarks Icon */}
            <IconButton
              aria-label="bookmarks"
              onClick={goToBookmarks}
              className={classes.iconButton}
              title="Bookmarks"
            >
              <BookmarkIcon />
            </IconButton>
            <IconButton
              aria-label="show 4 new mails"
              onClick={goToMessages}
              className={classes.iconButton}
              title="Messages"
            >
              <MailIcon />
            </IconButton>
           

            <IconButton
              aria-label="open settings"
              onClick={goToSettingsPage}
              className={classes.iconButton}
              title="Settings"
            >
              <SettingsIcon />
            </IconButton>

            <IconButton
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              className={classes.iconButton}
              title="Account"
            >
              <AccountCircle />
            </IconButton>
            </div>

            {/* Mobile Menu */}
            <div className={classes.sectionMobile}>
              <IconButton
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                className={classes.iconButton}
                title="More"
              >
                <MoreIcon />
              </IconButton>
            </div>
          </div>
        </Toolbar>
      </AppBar>
      </Collapse>

      {/* Hanging toggle tab */}
      <button
        className={classes.headerToggleTab}
        onClick={() => setIsCollapsed((prev) => {
          const next = !prev;
          localStorage.setItem("navbarCollapsed", String(next));
          return next;
        })}
        aria-label={isCollapsed ? "Show header" : "Hide header"}
        title={isCollapsed ? "Show header" : "Hide header"}
      >
        {isCollapsed ? (
          <ExpandMoreIcon fontSize="small" />
        ) : (
          <ExpandLessIcon fontSize="small" />
        )}
      </button>

      <RoomModal
        roomMenuOpen={roomMenuOpen}
        customRoomModalTabValue={customRoomModalTabValue}
        setRoomMenuOpen={setRoomMenuOpen}
      />

      <RoomJoinRequestsModal
        roomJoinRequestsOpen={roomJoinRequestsOpen}
        setRoomJoinRequestsOpen={setRoomJoinRequestsOpen}
      />

      <RoomTopMenuOptionsModal
        roomTopMenuOptionsOpen={roomTopMenuOptionsOpen}
        setRoomTopMenuOptionsOpen={setRoomTopMenuOptionsOpen}
        classes={classes}
        isCurrentUserMainAdmin={isCurrentUserMainAdmin}
        roomName={activeChat.name}
        roomId={activeChat.id}
      />

      {renderMobileMenu}
      {renderMenu}
    </div>
  );
}
